import { useState } from 'react';

/**
 * Public page for Stripe Checkout return from a CLI-initiated upgrade
 * (AIT-397). A CLI user pays in a browser with no dashboard session, so
 * landing on /org/:orgId/billing (behind ProtectedRoute) would bounce them
 * to AuthKit sign-in right after paying — the same login-wall problem
 * /cli/callback already solves for the Meta connect flow.
 *
 * This page makes no API call and needs no auth: the CLI (0.14.14+) polls
 * the subscription itself and prints success in the terminal, so all this
 * page has to do is tell the user what happened and send them back.
 */
type View = 'success' | 'cancelled' | 'unknown';

/**
 * Only an explicit `status=success` claims the payment landed. A missing or
 * unrecognized status means this page has no evidence either way, so it says
 * so instead of asserting a charge that may never have happened.
 */
function computeInitialView(): View {
  const status = new URLSearchParams(window.location.search).get('status');
  if (status === 'success') return 'success';
  if (status === 'cancelled') return 'cancelled';
  return 'unknown';
}

export function CliBillingCallbackPage() {
  const [view] = useState<View>(computeInitialView);

  const center: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontFamily: 'system-ui',
    padding: 24,
    textAlign: 'center',
    background: '#1C1E21',
    color: '#fff',
  };

  if (view === 'cancelled') {
    return (
      <div style={center}>
        <p>Checkout cancelled. Nothing was charged.</p>
      </div>
    );
  }

  if (view === 'unknown') {
    return (
      <div style={center}>
        <p>Return to your terminal. The CLI shows whether your upgrade went through.</p>
      </div>
    );
  }

  return (
    <div style={center}>
      <p>Payment received. Return to your terminal. The CLI is finishing your upgrade.</p>
    </div>
  );
}
